import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText, 
  Scale, 
  Check, 
  ArrowRight,
  Lock,
  Calendar,
  Video,
  CreditCard,
  Building2,
  FileSignature,
  TrendingUp,
  MapPin,
  Target,
  Search,
  User,
  LogIn,
  Mail
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../components/ui/accordion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo URLs
const LOGO_TERRACOTTA = "https://customer-assets.emergentagent.com/job_academy-session/artifacts/gtms0r7v_Logo%20Agence%20Immobilier%20Minimaliste%20Marron%20Gris%20Beige%20-%203.PNG";
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_academy-session/artifacts/0wu9ml1i_Logo%20Agence%20Immobilier%20Minimaliste%20Marron%20Gris%20Beige%20-%202.PNG";

// Curated royalty-free imagery (terracotta/cream editorial mix)
const IMAGES = {
  hero: "https://images.pexels.com/photos/7706385/pexels-photo-7706385.jpeg?auto=compress&cs=tinysrgb&w=1200",
  formatrice: "https://customer-assets.emergentagent.com/job_0a3047b7-ec9c-45f1-8913-b1ca2e6b0b1c/artifacts/8qw131kx_P1051813.JPG",
  inclus: "https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=1000&q=80",
  divider: "https://images.unsplash.com/photo-1481026469463-66327c86e544?auto=format&fit=crop&w=1600&q=80",
};

// Biens commerciaux (photos fournies par la formatrice)
const BIENS = [
  {
    img: "https://customer-assets.emergentagent.com/job_0a3047b7-ec9c-45f1-8913-b1ca2e6b0b1c/artifacts/2r7th691_photo%20restaurant.png",
    label: "Fonds de commerce & droit au bail",
    sub: "Cafés, restaurants, commerces de bouche",
  },
  {
    img: "https://customer-assets.emergentagent.com/job_0a3047b7-ec9c-45f1-8913-b1ca2e6b0b1c/artifacts/dmzb4s3h_photo%20open.png",
    label: "Commerces & locaux professionnels",
    sub: "Boutiques, locaux, changement de destination",
  },
  {
    img: "https://customer-assets.emergentagent.com/job_0a3047b7-ec9c-45f1-8913-b1ca2e6b0b1c/artifacts/0jysct07_photo%20hangar.png",
    label: "Murs commerciaux & investissement",
    sub: "Locaux d'activité, rentabilité, fiscalité",
  },
];

// Sessions passées / complètes (affichées comme preuve sociale, non réservables)
const SOLD_OUT_SESSIONS = [
  { id: 'soldout-2026-08-31', label: 'Lun. 31 août — Mar. 1er septembre 2026', lieu: 'Visio' },
];

// FAQ
const FAQ = [
  {
    q: "Ai-je besoin de prérequis pour suivre cette formation ?",
    a: "Cette formation est destinée aux agents / mandataires immobiliers, qu'ils soient débutants ou confirmés.",
  },
  {
    q: "Que se passe-t-il si je ne peux pas assister à une session en direct ?",
    a: "Prévenez-moi au moins 48h avant le début de la formation : votre place sera reportée sur une prochaine session sans frais supplémentaires.",
  },
  {
    q: "La formation est-elle enregistrée ? Puis-je la revoir ensuite ?",
    a: "La session se déroule en visio pour privilégier les échanges et cas pratiques. Vous repartez avec l'ensemble des supports (résumé, check-list, études de cas, barème) pour vous y référer à tout moment.",
  },
  {
    q: "Combien de participants par session ?",
    a: "10 places maximum, pour garantir un suivi personnalisé et des échanges directs sur vos propres dossiers.",
  },
  {
    q: "De quoi ai-je besoin pour suivre la formation ?",
    a: "Simplement d'une connexion internet stable, aucun logiciel spécifique n'est requis — les documents et outils vous seront transmis directement.",
  },
];

// Affiche "Visio" au lieu de "Zoom"
const displayLieu = (lieu) => (lieu && lieu.toLowerCase() === 'zoom' ? 'Visio' : (lieu || 'Visio'));


// Modules data with tags and icons
const MODULES = [
  { 
    num: 1, 
    title: "Le bail commercial", 
    desc: "Aspect juridique, droit au renouvellement, le bail professionnel, les clauses essentielles du bail, lecture d'un bail commercial et d'un renouvellement.",
    icon: FileSignature,
    tags: ["Droit au renouvellement", "Bail professionnel", "Clauses essentielles"],
    color: "terra"
  },
  { 
    num: 2, 
    title: "Les cessions", 
    desc: "Cession de fonds de commerce et de droit au bail, aspects juridiques, honoraires, rapports notaires/avocats, formulaire de découverte, licences d'alcool, diagnostics, cession de titres, valorisation (étude de cas), BODACC, annonces, exclusivité — comment sécuriser les ventes et gagner du temps.",
    icon: FileText,
    tags: ["Fonds & droit au bail", "Valorisation", "BODACC"],
    color: "brown"
  },
  { 
    num: 3, 
    title: "Les murs commerciaux", 
    desc: "Investissements, fiscalité, liste des documents à demander, droit de préférence, calcul de rentabilité, risques et points de vigilance, études de cas, valorisation de murs loués et vacants.",
    icon: Building2,
    tags: ["Rentabilité", "Fiscalité", "Droit de préférence"],
    color: "terra"
  },
  { 
    num: 4, 
    title: "La location professionnelle", 
    desc: "Les enjeux, le changement de destination, la valorisation, la rédaction du bail.",
    icon: MapPin,
    tags: ["Changement destination", "Valorisation", "Rédaction du bail"],
    color: "brown"
  },
  { 
    num: 5, 
    title: "Identifier un bien attractif", 
    desc: "Conseils et méthodes pour prendre de bons mandats.",
    icon: Target,
    tags: ["Bons mandats", "Analyse terrain"],
    color: "terra"
  },
  { 
    num: 6, 
    title: "La prospection", 
    desc: "Tips de prospection sur le terrain et sur internet, trouver les coordonnées des propriétaires en ligne.",
    icon: Search,
    tags: ["Terrain", "Internet", "Coordonnées propriétaires"],
    color: "brown"
  },
];

// Timeline data
const TIMELINE = [
  { 
    timing: "Aujourd'hui", 
    title: "Réservation & paiement", 
    desc: "Vous réservez votre place et payez en ligne par carte bancaire (Stripe). Confirmation immédiate par email." 
  },
  { 
    timing: "Avant la session", 
    title: "Préparation", 
    desc: "Questionnaire de préparation et ressources de pré-lecture pour adapter la journée à votre niveau." 
  },
  { 
    timing: "Jour J", 
    title: "Formation intensive", 
    desc: "6 modules, quiz, études de cas sur vos dossiers réels. 10 participants maximum pour un suivi personnalisé." 
  },
  { 
    timing: "Après la session", 
    title: "Ressources & attestation", 
    desc: "Toutes les annexes, checklists et votre attestation officielle de formation." 
  },
];

// Testimonials data (avis clients réels — Immodvisor)
const TESTIMONIALS = [
  {
    title: "Nous recommandons sans hésitation",
    quote: "Nous avons eu la chance d'être accompagnés par Nadège pour l'achat de notre restaurant, et nous ne pouvions espérer meilleure expérience. Disponible, réactive et toujours de bon conseil, elle a su nous guider avec professionnalisme et bienveillance à chaque étape du processus."
  },
  {
    title: "Extrêmement satisfait",
    quote: "Extrêmement satisfait du travail de Mme Ledru. Excellente expertise à tous points de vue. Efficace, disponible, à l'écoute, très sympathique. Dossier traité avec rapidité, clarté, mise en confiance pour tous les acteurs. Je recommande très vivement."
  },
  {
    title: "Prestation parfaite dans une opération complexe",
    quote: "Grand merci à une spécialiste de l'immobilier qui s'est occupée de tout avec brio et rapidité. Rare de trouver des agents immobiliers aussi performants et en qui on peut faire confiance à 100% pour toutes les formalités administratives et techniques. Bravo."
  },
  {
    title: "Parfait de A à Z",
    quote: "Je recommande vivement Nadège Ledru pour toute vente de fonds de commerce ou de droit au bail. C'est une professionnelle d'une grande honnêteté, ce qui est suffisamment rare dans ce secteur pour être souligné. Contrairement à certains agents qui enjolivent la réalité, elle ne fait jamais de promesses irréalistes : elle s'appuie sur une évaluation juste, transparente et fondée. Elle effectue un véritable travail de fond, en ciblant avec soin les acquéreurs potentiels pour chaque bien. On sent qu'elle connaît son métier, qu'elle prend le temps de chercher les bons profils, et qu'elle ne se contente pas de diffuser une annonce : elle va activement à la rencontre des acheteurs intéressés. Autre point très appréciable : elle assure un excellent suivi. Après chaque visite, elle donne des nouvelles, fait le point, informe avec clarté, ce qui rend la démarche beaucoup plus humaine et rassurante. Et lorsque la vente se concrétise, elle suit chaque étape du dossier avec rigueur et implication, jusqu'à la signature finale. Aucun détail n'est laissé au hasard. Grâce à son professionnalisme, sa transparence, ses conseils et son accompagnement attentif, j'ai pu vendre dans les meilleures conditions possibles. Je lui en suis profondément reconnaissante et je la recommande sans hésitation. Un grand merci."
  },
  {
    title: "Excellente qualité de service",
    quote: "Madame Nadège Ledru a fait son travail d'agent immobilier avec qualité du début de notre rencontre jusqu'à la signature définitive de la vente de notre bien. Nous avons été toujours parfaitement accompagnés. Nous n'avons qu'à nous louer des prestations de Madame Ledru et nous ne manquerons pas de la conseiller à nos connaissances."
  },
];

// Ce qui est inclus — outils remis à l'issue de la formation
const INCLUDED = [
  { icon: FileText, label: "Résumé de la formation" },
  { icon: Search, label: "Formulaire de découverte" },
  { icon: Check, label: "Check-list" },
  { icon: TrendingUp, label: "Barème de valorisation" },
  { icon: Scale, label: "Études de cas" },
];
const Tag = ({ children, variant = "terra" }) => {
  const variants = {
    terra: "bg-terra/10 text-terra border-terra/20",
    brown: "bg-brown/10 text-brown border-brown/20",
    light: "bg-cream-dark text-brown-mid border-sand"
  };
  
  return (
    <span className={`inline-block px-3 py-1 text-xs font-body font-medium border ${variants[variant]}`}>
      {children}
    </span>
  );
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [availableSessions, setAvailableSessions] = useState([]);
  const [formData, setFormData] = useState({ prenom: '', email: '', reseau: '', session_id: '', promo_code: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [promoResult, setPromoResult] = useState(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginType, setLoginType] = useState(null); // 'admin' or 'student'
  const [loginData, setLoginData] = useState({ email: '', password: '', token: '' });
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [registeredData, setRegisteredData] = useState(null);
  const [gaugeAnimated, setGaugeAnimated] = useState(false);
  
  const bookingCardRef = useRef(null);
  const prenomInputRef = useRef(null);

  useEffect(() => {
    fetchSession();
    fetchAvailableSessions();
    
    // Trigger gauge animation after delay
    const timer = setTimeout(() => setGaugeAnimated(true), 600);
    
    // Setup Intersection Observer for reveal animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  // Re-validate promo when the selected session changes
  useEffect(() => {
    if (promoResult && formData.promo_code.trim()) {
      validatePromo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.session_id]);

  const fetchSession = async () => {
    try {
      const res = await axios.get(`${API}/session`);
      setSession(res.data);
    } catch (error) {
      console.error('Error fetching session:', error);
    }
  };

  const fetchAvailableSessions = async () => {
    try {
      const res = await axios.get(`${API}/training-sessions/available`);
      setAvailableSessions(res.data);
      const selectable = res.data.filter(s => !s.is_full);
      if (selectable.length === 1) {
        setFormData(prev => ({ ...prev, session_id: selectable[0].id }));
      }
    } catch (error) {
      console.error('Error fetching available sessions:', error);
    }
  };

  const scrollToBooking = () => {
    const card = bookingCardRef.current;
    if (card) card.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => {
      const input = prenomInputRef.current;
      if (input) input.focus();
    }, 600);
  };

  const validatePromo = async () => {
    const code = formData.promo_code.trim();
    if (!code) {
      setPromoResult(null);
      return;
    }
    setIsValidatingPromo(true);
    try {
      const res = await axios.post(`${API}/promo/validate`, {
        code,
        session_id: formData.session_id || null,
      });
      setPromoResult(res.data);
      if (res.data.valid) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message || 'Code promo invalide');
      }
    } catch (error) {
      setPromoResult(null);
      toast.error('Impossible de vérifier le code promo');
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.prenom.trim()) {
      toast.error('Veuillez entrer votre prénom');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Veuillez entrer un email valide');
      return;
    }

    if (selectableSessions.length > 0 && !formData.session_id) {
      toast.error('Veuillez choisir un créneau de formation');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const payload = {
        prenom: formData.prenom,
        email: formData.email,
        reseau: formData.reseau,
        session_id: formData.session_id || null,
        promo_code: formData.promo_code.trim() || null
      };
      const res = await axios.post(`${API}/register`, payload);
      setRegisteredData(res.data);
      setShowModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setIsSubmitting(false);
    }
  };

  const redirectToPayment = async () => {
    if (!registeredData?.token) {
      setShowModal(false);
      return;
    }
    setIsPaying(true);
    try {
      const res = await axios.post(`${API}/payment/${registeredData.token}/stripe-checkout`, {
        origin_url: window.location.origin
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Lien de paiement indisponible. Réessayez plus tard.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du paiement');
    } finally {
      setIsPaying(false);
    }
  };

  const openLoginModal = (type) => {
    setLoginType(type);
    setLoginData({ email: '', password: '', token: '' });
    setShowLoginModal(true);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);

    try {
      if (loginType === 'admin') {
        const res = await axios.post(`${API}/admin/login`, {
          email: loginData.email,
          password: loginData.password
        });
        if (res.data.success) {
          localStorage.setItem('admin_token', res.data.token);
          localStorage.setItem('admin_email', res.data.admin.email);
          localStorage.setItem('admin_name', res.data.admin.name);
          toast.success('Connexion réussie');
          setShowLoginModal(false);
          navigate('/admin/dashboard');
        }
      } else {
        // Student login with token
        const res = await axios.get(`${API}/student/${loginData.token}`);
        if (res.data.participant) {
          toast.success(`Bienvenue ${res.data.participant.prenom}`);
          setShowLoginModal(false);
          navigate(`/student/${loginData.token}`);
        }
      }
    } catch (error) {
      if (loginType === 'admin') {
        toast.error('Email ou mot de passe incorrect');
      } else {
        toast.error('Token invalide ou expiré');
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const selectedSession = availableSessions.find(s => s.id === formData.session_id);
  const selectableSessions = availableSessions.filter(s => !s.is_full);
  const fullSessions = availableSessions.filter(s => s.is_full);
  const totalPlaces = selectedSession ? selectedSession.places_total : (session?.places_total || 10);
  const takenPlaces = selectedSession ? selectedSession.places_prises : (session?.places_prises || 0);
  const gaugePercent = totalPlaces > 0 ? (takenPlaces / totalPlaces) * 100 : 0;
  const displayTarif = selectedSession ? selectedSession.tarif : (session?.tarif || 300);
  const promoApplied = promoResult && promoResult.valid;
  const effectiveTarif = promoApplied ? promoResult.discounted_amount : displayTarif;

  return (
    <div className="min-h-screen bg-cream">
      {/* Navigation */}
      <nav className="nav-fixed fixed top-0 left-0 right-0 z-50 py-4 px-8 lg:px-16 flex items-center justify-between">
        <img 
          src={LOGO_TERRACOTTA} 
          alt="L'Académie de l'Immobilier Commercial" 
          className="h-10 object-contain"
          data-testid="nav-logo"
        />
        <div className="flex items-center gap-4">
          {/* Login Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="flex items-center gap-2 px-4 py-2 text-brown-mid hover:text-brown transition-colors font-body text-sm"
                data-testid="nav-login-button"
              >
                <User size={18} />
                <span className="hidden sm:inline">Connexion</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-white border border-sand">
              <DropdownMenuItem 
                onClick={() => openLoginModal('admin')}
                className="flex items-center gap-3 cursor-pointer py-3 px-4 hover:bg-cream focus:bg-cream"
                data-testid="login-admin-option"
              >
                <div className="w-8 h-8 bg-terra/10 flex items-center justify-center">
                  <Lock size={16} className="text-terra" />
                </div>
                <div>
                  <p className="font-body text-sm font-medium text-brown">Espace Admin</p>
                  <p className="font-body text-xs text-brown-light">Gérer les formations</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-sand" />
              <DropdownMenuItem 
                onClick={() => openLoginModal('student')}
                className="flex items-center gap-3 cursor-pointer py-3 px-4 hover:bg-cream focus:bg-cream"
                data-testid="login-student-option"
              >
                <div className="w-8 h-8 bg-brown/10 flex items-center justify-center">
                  <User size={16} className="text-brown-mid" />
                </div>
                <div>
                  <p className="font-body text-sm font-medium text-brown">Espace Participant</p>
                  <p className="font-body text-xs text-brown-light">Questionnaires & attestation</p>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button 
            onClick={scrollToBooking}
            className="cta-button hidden sm:block"
            data-testid="nav-cta-button"
          >
            Réserver ma place — {displayTarif} €
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen pt-24 lg:pt-0 lg:grid lg:grid-cols-2">
        {/* Left Column */}
        <div className="hero-split-left relative px-8 lg:px-16 py-16 lg:py-0 lg:flex lg:flex-col lg:justify-center">
          <div className="max-w-xl relative z-10">
            <h1 className="animate-fade-up delay-1 mb-6">
              <span className="block font-display text-5xl lg:text-7xl font-light text-brown tracking-tight">Comprendre</span>
              <span className="block font-display text-5xl lg:text-7xl font-light italic text-terra tracking-tight">l&apos;immobilier</span>
              <span className="block font-display text-5xl lg:text-7xl font-semibold text-brown tracking-tight">commercial</span>
            </h1>
            
            <div className="w-12 h-px bg-terra animate-fade-up delay-2 mb-8" />
            
            <p className="font-body text-base text-brown-mid leading-relaxed animate-fade-up delay-3 mb-6" data-testid="hero-description">
              La formation complète pour maîtriser le <span className="text-terra font-medium">bail commercial</span>, les <span className="text-terra font-medium">cessions de fonds de commerce et droit au bail</span>, les <span className="text-terra font-medium">murs commerciaux</span> et la <span className="text-terra font-medium">prospection</span> — des outils directement applicables à vos dossiers.
            </p>
            
            {/* Key terms badges */}
            <div className="flex flex-wrap gap-2 animate-fade-up delay-3 mb-10">
              <Tag variant="terra">Bail 3/6/9</Tag>
              <Tag variant="brown">Cession fonds</Tag>
              <Tag variant="terra">Murs commerciaux</Tag>
              <Tag variant="brown">Prospection</Tag>
            </div>
            
            {/* Session Info Strip */}
            <div className="session-strip grid grid-cols-1 sm:grid-cols-3 animate-fade-up delay-3 mb-8">
              <div className="session-strip-item p-4 text-center sm:text-left">
                <span className="form-label block mb-1">DATE</span>
                <span className="font-display text-lg font-semibold text-brown flex items-center justify-center sm:justify-start gap-2">
                  <Calendar size={16} className="text-terra" />
                  {selectedSession 
                    ? `${new Date(selectedSession.date_jour1 + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} — ${new Date(selectedSession.date_jour2 + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`
                    : (availableSessions.length > 0 ? 'Choisir un créneau' : (session?.date_session || 'À confirmer'))
                  }
                </span>
              </div>
              <div className="session-strip-item p-4 text-center sm:text-left">
                <span className="form-label block mb-1">FORMAT</span>
                <span className="font-display text-lg font-semibold text-brown flex items-center justify-center sm:justify-start gap-2">
                  <Video size={16} className="text-terra" />
                  {selectedSession ? `${displayLieu(selectedSession.lieu)} · 2 jours` : 'Visio · 2 jours'}
                </span>
                <span className="block font-body text-xs text-brown-mid mt-1">9h–12h · 13h–16h chaque jour</span>
              </div>
              <div className="session-strip-item p-4 text-center sm:text-left">
                <span className="form-label block mb-1">TARIF</span>
                <span className="font-display text-lg font-semibold text-terra flex items-center justify-center sm:justify-start gap-2">
                  <CreditCard size={16} className="text-terra" />
                  {displayTarif} €
                </span>
              </div>
            </div>
            
            {/* Places Gauge */}
            <div className="max-w-md animate-fade-up delay-4" data-testid="places-gauge">
              <div className="flex items-center justify-between mb-2">
                <span className="form-label">PLACES DISPONIBLES</span>
              </div>
              <div className="gauge-track">
                <div 
                  className="gauge-fill transition-all duration-1000"
                  style={{ width: gaugeAnimated ? `${gaugePercent}%` : '0%' }}
                  data-testid="gauge-fill"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Booking Card */}
        <div className="hero-split-right relative overflow-hidden px-8 lg:px-16 py-16 lg:py-0 lg:flex lg:items-center lg:justify-center" ref={bookingCardRef}>
          <img
            src={IMAGES.hero}
            alt="Immobilier commercial"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="hero-image"
          />
          <div className="absolute inset-0 bg-brown/45 mix-blend-multiply pointer-events-none" />
          <div className="absolute inset-0 bg-terra/10 pointer-events-none" />
          <div className="booking-card w-full max-w-md animate-fade-up delay-2 relative z-10" data-testid="booking-card">
            {/* Card Header */}
            <div className="bg-brown p-6 flex items-center justify-between">
              <h2 className="font-display text-xl text-cream">Réserver ma place</h2>
              <div className="text-right">
                <span className="font-display text-3xl font-semibold text-terra-light">{displayTarif} €</span>
                <span className="block font-body text-xs uppercase tracking-wider text-cream/60">par participant</span>
              </div>
            </div>
            
            {/* Card Body */}
            <form onSubmit={handleSubmit} className="p-6 lg:p-8 space-y-5">
              <div>
                <label className="form-label block mb-2">PRÉNOM</label>
                <input
                  ref={prenomInputRef}
                  type="text"
                  className="form-input w-full"
                  value={formData.prenom}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  placeholder="Votre prénom"
                  data-testid="input-prenom"
                />
              </div>
              
              <div>
                <label className="form-label block mb-2">EMAIL</label>
                <input
                  type="email"
                  className="form-input w-full"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="votre@email.com"
                  data-testid="input-email"
                />
              </div>
              
              <div>
                <label className="form-label block mb-2">RÉSEAU / AGENCE</label>
                <input
                  type="text"
                  className="form-input w-full"
                  value={formData.reseau}
                  onChange={(e) => setFormData({ ...formData, reseau: e.target.value })}
                  placeholder="Ex: IAD, Century 21, Indépendant..."
                  data-testid="input-reseau"
                />
              </div>

              {/* Session Selection */}
              {(availableSessions.length > 0 || SOLD_OUT_SESSIONS.length > 0) && (
                <div>
                  <label className="form-label block mb-2">CRÉNEAU DE FORMATION</label>
                  <select
                    className="form-input w-full"
                    value={formData.session_id}
                    onChange={(e) => setFormData({ ...formData, session_id: e.target.value })}
                    data-testid="select-session"
                  >
                    {selectableSessions.length !== 1 && (
                      <option value="">Choisir un créneau...</option>
                    )}
                    {selectableSessions.map((s) => (
                      <option key={s.id} value={s.id}>
                        Lun. {new Date(s.date_jour1 + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} — Mar. {new Date(s.date_jour2 + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })} · {displayLieu(s.lieu)}
                      </option>
                    ))}
                    {fullSessions.map((s) => (
                      <option key={s.id} value="" disabled>
                        Lun. {new Date(s.date_jour1 + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} — Mar. {new Date(s.date_jour2 + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })} · {displayLieu(s.lieu)} · Complet
                      </option>
                    ))}
                    {SOLD_OUT_SESSIONS.map((s) => (
                      <option key={s.id} value="" disabled data-testid={`soldout-${s.id}`}>
                        {s.label} · {displayLieu(s.lieu)} · Complet
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Promo Code */}
              <div>
                <label className="form-label block mb-2">CODE PROMO</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="form-input w-full uppercase"
                    value={formData.promo_code}
                    onChange={(e) => {
                      setFormData({ ...formData, promo_code: e.target.value });
                      if (promoResult) setPromoResult(null);
                    }}
                    onBlur={validatePromo}
                    placeholder="Entrez votre code"
                    data-testid="input-promo"
                  />
                  <button
                    type="button"
                    onClick={validatePromo}
                    disabled={isValidatingPromo || !formData.promo_code.trim()}
                    className="px-4 border border-brown text-brown font-body text-sm hover:bg-brown hover:text-cream transition-colors disabled:opacity-40 whitespace-nowrap"
                    data-testid="apply-promo-button"
                  >
                    {isValidatingPromo ? '...' : 'Appliquer'}
                  </button>
                </div>
                {promoResult && (
                  <p
                    className={`font-body text-xs mt-2 ${promoApplied ? 'text-terra font-medium' : 'text-red-500'}`}
                    data-testid="promo-feedback"
                  >
                    {promoApplied ? `✓ ${promoResult.message}` : promoResult.message}
                  </p>
                )}
              </div>
              
              {/* Order Summary */}
              <div className="bg-cream border border-sand p-4 space-y-3 mt-6">
                <p className="font-body text-xs uppercase tracking-wider text-brown-mid">Récap commande :</p>
                <div className="flex justify-between text-sm">
                  <span className="text-brown">Formation immobilier commercial</span>
                  <span className="font-semibold text-terra">{displayTarif} €</span>
                </div>
                {promoApplied && (
                  <div className="flex justify-between text-sm" data-testid="promo-discount-line">
                    <span className="text-brown">Code promo {formData.promo_code.trim().toUpperCase()}</span>
                    <span className="font-semibold text-terra">− {displayTarif - effectiveTarif} €</span>
                  </div>
                )}
                <div className="flex justify-between text-sm text-brown-mid">
                  <span>2 jours (9h–12h · 13h–16h) · 6 modules · Annexes</span>
                  <span>—</span>
                </div>
                <div className="border-t border-sand pt-3 flex justify-between items-center">
                  <span className="font-body text-sm font-medium text-brown">TOTAL</span>
                  <span className="font-display text-xl font-semibold text-terra-dark" data-testid="order-total">
                    {promoApplied && (
                      <span className="font-body text-sm text-brown-light line-through mr-2">{displayTarif} €</span>
                    )}
                    {effectiveTarif} €
                  </span>
                </div>
              </div>
              
              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="cta-button w-full flex items-center justify-center gap-2 mt-4 disabled:opacity-50"
                data-testid="submit-booking-button"
              >
                {isSubmitting ? 'Traitement...' : 'Payer et réserver'}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
              
              <p className="text-center text-sm text-brown-light flex items-center justify-center gap-2">
                <Lock size={14} />
                Paiement sécurisé par carte bancaire
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Proof Strip */}
      <section className="proof-strip py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0 lg:divide-x lg:divide-white/10">
            {[
              { num: '6', text: 'modules complets' },
              { num: '10', text: 'places max / session' },
              { num: '2', text: 'jours intensifs' },
              { num: '100%', text: 'cas pratiques terrain' },
            ].map((item, i) => (
              <div key={i} className="text-center lg:px-8" data-testid={`proof-item-${i}`}>
                <span className="font-display text-4xl font-light text-terra-light">{item.num}</span>
                <p className="font-body text-sm text-cream/65 mt-2">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programme Section */}
      <section className="py-20 lg:py-28 px-8" data-testid="programme-section">
        <div className="max-w-6xl mx-auto">
          <p className="eyebrow mb-4 reveal">Programme</p>
          <h2 className="font-display text-4xl lg:text-5xl font-light text-brown mb-4 reveal">
            Ce que vous allez <em className="italic text-terra">maîtriser</em>
          </h2>
          <p className="font-body text-lg lg:text-xl text-brown-mid max-w-2xl mb-12 reveal">
            6 modules progressifs, de la <span className="text-terra font-medium">théorie juridique</span> aux <span className="text-terra font-medium">réflexes terrain</span> — en deux jours seulement.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-sand border border-sand">
            {MODULES.map((module) => (
              <div 
                key={module.num} 
                className="module-card bg-white p-8 lg:p-10 reveal transition-colors duration-200 group"
                data-testid={`module-card-${module.num}`}
              >
                {/* Module Header */}
                <div className="flex items-start justify-between mb-4">
                  <span className="module-number font-display text-5xl font-light text-sand transition-colors duration-200 group-hover:text-terra-light">
                    {module.num}
                  </span>
                  <div className={`w-12 h-12 flex items-center justify-center border ${
                    module.color === 'terra' ? 'border-terra/30 text-terra' : 'border-brown/20 text-brown-mid'
                  }`}>
                    <module.icon size={24} />
                  </div>
                </div>
                
                {/* Module Title */}
                <h3 className="font-display text-xl font-semibold text-brown mb-3">
                  {module.title}
                </h3>
                
                {/* Module Description */}
                <p className="font-body text-sm text-brown-mid leading-relaxed mb-4">
                  {module.desc}
                </p>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {module.tags.map((tag, i) => (
                    <span 
                      key={i}
                      className={`inline-block px-2 py-1 text-xs font-body ${
                        module.color === 'terra' 
                          ? 'bg-terra/10 text-terra' 
                          : 'bg-brown/10 text-brown-mid'
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Biens Commerciaux Gallery */}
      <section className="py-20 lg:py-28 px-8 bg-cream-dark" data-testid="biens-section">
        <div className="max-w-6xl mx-auto">
          <p className="eyebrow mb-4 reveal">En pratique</p>
          <h2 className="font-display text-4xl lg:text-5xl font-light text-brown mb-4 reveal">
            Les biens que vous allez <em className="italic text-terra">maîtriser</em>
          </h2>
          <p className="font-body text-base text-brown-mid max-w-2xl mb-12 reveal">
            De la terrasse de café au local d&apos;activité : des cas concrets, tirés du terrain, pour chaque type de bien commercial.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {BIENS.map((bien, i) => (
              <div
                key={i}
                className="group relative overflow-hidden border border-sand aspect-[4/5] reveal"
                style={{ transitionDelay: `${i * 0.1}s` }}
                data-testid={`bien-card-${i}`}
              >
                <img
                  src={bien.img}
                  alt={bien.label}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  data-testid={`bien-image-${i}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brown/85 via-brown/20 to-transparent pointer-events-none" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-display text-xl font-semibold text-cream mt-1 leading-tight">{bien.label}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* La Formatrice Section */}
      <section className="py-20 lg:py-28 px-8" data-testid="formatrice-section">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Portrait */}
          <div className="lg:col-span-5 relative reveal">
            <div className="absolute -top-4 -left-4 w-full h-full bg-terra/15 hidden lg:block pointer-events-none" />
            <div className="relative overflow-hidden border border-sand aspect-[3/4] bg-cream-dark">
              <img
                src={IMAGES.formatrice}
                alt="Nadège Ledru, formatrice en immobilier commercial"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 ease-out hover:scale-105"
                data-testid="formatrice-image"
              />
              <div className="absolute inset-0 bg-terra/10 mix-blend-multiply pointer-events-none" />
            </div>
          </div>
          {/* Bio */}
          <div className="lg:col-span-7 reveal">
            <p className="eyebrow mb-4">La formatrice</p>
            <h2 className="font-display text-4xl lg:text-5xl font-light text-brown mb-6">
              Nadège <em className="italic text-terra">Ledru</em>
            </h2>
            <div className="w-12 h-px bg-terra mb-6" />
            <p className="font-body text-base lg:text-lg text-brown-mid leading-relaxed mb-5">
              Professionnelle de l&apos;immobilier depuis 20 ans, Nadège accompagne au quotidien acheteurs et vendeurs de <span className="text-terra font-medium">fonds de commerce</span>, <span className="text-terra font-medium">droit au bail</span> et <span className="text-terra font-medium">murs commerciaux</span>. Une expertise saluée pour sa rigueur, sa transparence et son accompagnement de A à Z.
            </p>
            <p className="font-body text-base text-brown-mid leading-relaxed mb-8">
              Dans cette formation, elle transmet ses <span className="text-terra font-medium">méthodes de terrain</span> — celles qui font la différence sur un dossier réel — pour vous rendre opérationnel dès le lendemain.
            </p>
            <div className="flex flex-wrap gap-3">
              <div className="border border-sand bg-cream px-5 py-3" data-testid="formatrice-stat-0">
                <p className="font-display text-2xl font-semibold text-terra">5/5</p>
                <p className="font-body text-xs text-brown-light uppercase tracking-wider">Avis clients vérifiés</p>
              </div>
              <div className="border border-sand bg-cream px-5 py-3" data-testid="formatrice-stat-1">
                <p className="font-display text-2xl font-semibold text-terra">100%</p>
                <p className="font-body text-xs text-brown-light uppercase tracking-wider">Cas pratiques terrain</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 lg:py-28 px-8 bg-cream-dark" data-testid="timeline-section">
        <div className="max-w-6xl mx-auto">
          <p className="eyebrow mb-4 reveal">Déroulement</p>
          <h2 className="font-display text-4xl lg:text-5xl font-light text-brown mb-12 reveal">
            Comment ça <em className="italic text-terra">fonctionne</em>
          </h2>
          
          <div className="relative">
            <div className="timeline-connector" />
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
              {TIMELINE.map((step, i) => (
                <div key={i} className="relative reveal" style={{ transitionDelay: `${i * 0.08}s` }} data-testid={`timeline-step-${i}`}>
                  <div className="w-12 h-12 border border-terra bg-cream-dark flex items-center justify-center mb-4 relative z-10">
                    <span className="font-display text-lg font-semibold text-terra">{i + 1}</span>
                  </div>
                  <span className="font-body text-xs uppercase tracking-wider text-terra font-medium">{step.timing}</span>
                  <h3 className="font-display text-lg font-semibold text-brown mt-2 mb-2">{step.title}</h3>
                  <p className="font-body text-sm text-brown-mid leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Editorial Divider Band */}
      <section className="relative overflow-hidden py-24 lg:py-32 px-8" data-testid="editorial-divider">
        <img
          src={IMAGES.divider}
          alt="Architecture immobilier commercial"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-brown/85 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto text-center reveal">
          <p className="eyebrow text-terra-light mb-6">L&apos;immobilier commercial, autrement</p>
          <h2 className="font-display text-3xl lg:text-5xl font-light text-cream leading-tight">
            « Des compétences rares, une approche concrète et un <em className="italic text-terra-light">accompagnement de A à Z</em>. »
          </h2>
          <p className="font-body text-cream/60 mt-8 text-sm uppercase tracking-[0.2em]">
            6 modules · 2 jours · directement applicable
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 lg:py-28 px-8" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto">
          <p className="eyebrow mb-4 reveal">Avis clients</p>
          <h2 className="font-display text-4xl lg:text-5xl font-light text-brown mb-12 reveal">
            Quelques <em className="italic text-terra">avis clients</em>
          </h2>
          
          <div className="columns-1 md:columns-2 gap-6 [column-fill:_balance]">
            {TESTIMONIALS.map((testimonial, i) => (
              <div 
                key={i} 
                className="relative bg-white border border-sand p-8 pt-10 mb-6 break-inside-avoid reveal"
                data-testid={`testimonial-${i}`}
              >
                <span className="testimonial-quote">&quot;</span>
                <h3 className="font-display text-xl font-semibold text-terra mb-3">
                  {testimonial.title}
                </h3>
                <p className="font-body text-sm text-brown-mid leading-relaxed mb-5">
                  {testimonial.quote}
                </p>
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-terra" />
                  <span className="font-body text-xs text-brown-light">Avis vérifié · Immodvisor</span>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-center text-sm text-brown-mid mt-8 reveal" data-testid="immodvisor-mention">
            Avis vérifiés et authentiques recueillis grâce à <span className="font-medium text-brown">Immodvisor</span>
          </p>
        </div>
      </section>

      {/* Ce qui est inclus Section */}
      <section className="py-20 lg:py-28 px-8" data-testid="included-section">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Image */}
          <div className="lg:col-span-5 reveal order-2 lg:order-1">
            <div className="relative overflow-hidden border border-sand aspect-[4/5] bg-cream-dark">
              <img
                src={IMAGES.inclus}
                alt="Documents et outils de la formation"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 ease-out hover:scale-105"
                data-testid="inclus-image"
              />
              <div className="absolute inset-0 bg-terra/10 mix-blend-multiply pointer-events-none" />
            </div>
          </div>
          {/* Content */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <p className="eyebrow mb-4 reveal">Inclus</p>
            <h2 className="font-display text-4xl lg:text-5xl font-light text-brown mb-4 reveal">
              Ce qui est <em className="italic text-terra">inclus</em>
            </h2>
            <p className="font-body text-base text-brown-mid max-w-2xl mb-10 reveal">
              Vous repartez avec tous les documents et outils qui vous permettront de travailler immédiatement.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {INCLUDED.map((item, i) => (
                <div 
                  key={i} 
                  className="bg-white border border-sand p-6 flex items-center gap-4 reveal"
                  style={{ transitionDelay: `${i * 0.08}s` }}
                  data-testid={`included-${i}`}
                >
                  <div className="guarantee-icon">
                    <item.icon size={18} />
                  </div>
                  <h3 className="font-display text-base font-semibold text-brown">{item.label}</h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-28 px-8 bg-cream-dark" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <p className="eyebrow mb-4 reveal">Questions fréquentes</p>
          <h2 className="font-display text-4xl lg:text-5xl font-light text-brown mb-12 reveal">
            Vous vous <em className="italic text-terra">demandez</em> ?
          </h2>
          <Accordion type="single" collapsible className="reveal">
            {FAQ.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border-b border-sand"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger
                  className="font-display text-lg lg:text-xl font-medium text-brown text-left hover:text-terra hover:no-underline py-5"
                  data-testid={`faq-question-${i}`}
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="font-body text-base text-brown-mid leading-relaxed pb-6">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>


      {/* CTA Final Section */}
      <section className="cta-final py-20 lg:py-28 px-8 text-center" data-testid="cta-final-section">
        <div className="max-w-3xl mx-auto relative z-10">
          <h2 className="font-display text-4xl lg:text-5xl font-light text-cream mb-6 reveal">
            Prêt(e) à développer<br />
            votre activité sur <em className="italic text-terra-light">l&apos;immo commercial</em> ?
          </h2>
          
          <p className="font-body text-base text-cream/60 mb-10 reveal">
            Une formation intensive de 2 jours, en petit groupe de 10 participants maximum pour un suivi personnalisé.
          </p>
          
          <button 
            onClick={scrollToBooking}
            className="cta-button cta-button-primary inline-flex items-center gap-2 reveal"
            data-testid="cta-final-button"
          >
            Réserver ma place — {displayTarif} €
            <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer py-10 px-8 lg:px-16">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-6">
          <img 
            src={LOGO_DARK} 
            alt="L'Académie de l'Immobilier Commercial" 
            className="h-9 object-contain invert brightness-75"
            data-testid="footer-logo"
          />
          <p className="footer-text text-center">
            © 2026 L&apos;Académie de l&apos;Immobilier Commercial · Tous droits réservés
          </p>
          <p className="footer-text">
            Contenu protégé · Reproduction interdite
          </p>
        </div>
      </footer>

      {/* Confirmation Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-white border border-sand p-0 max-w-md animate-scale-in" data-testid="confirmation-modal">
          <DialogTitle className="sr-only">Confirmation d&apos;inscription</DialogTitle>
          <DialogDescription className="sr-only">Votre inscription a été enregistrée</DialogDescription>
          <div className="p-8 text-center">
            <div className="w-16 h-16 border-2 border-terra mx-auto mb-6 flex items-center justify-center">
              <Check size={32} className="text-terra" />
            </div>
            
            <h3 className="font-display text-2xl text-brown mb-3">Inscription enregistrée</h3>
            <p className="font-body text-sm text-brown-mid mb-6">
              Vos informations ont bien été reçues. Vous allez être redirigé(e) vers le paiement sécurisé par carte bancaire.
            </p>
            
            <div className="bg-cream border border-sand p-4 text-left mb-6">
              <p className="font-body text-sm text-brown">
                <strong>Formation immobilier commercial</strong><br />
                {selectedSession 
                  ? `${new Date(selectedSession.date_jour1 + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} — ${new Date(selectedSession.date_jour2 + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })} · ${displayLieu(selectedSession.lieu)}`
                  : 'Visio · 2 jours'
                } · Montant : <strong className="text-terra">{effectiveTarif} €</strong>
              </p>
            </div>
            
            <button 
              onClick={redirectToPayment}
              disabled={isPaying}
              className="cta-button cta-button-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="proceed-payment-button"
            >
              {isPaying ? 'Redirection...' : 'Payer par carte bancaire'}
              <ArrowRight size={16} />
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Login Modal */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="bg-white border border-sand p-0 max-w-md animate-scale-in" data-testid="login-modal">
          <DialogTitle className="sr-only">
            {loginType === 'admin' ? 'Connexion Admin' : 'Accès Participant'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {loginType === 'admin' ? 'Connectez-vous à votre espace admin' : 'Accédez à votre espace participant'}
          </DialogDescription>
          
          {/* Modal Header */}
          <div className="bg-brown p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-terra/20 flex items-center justify-center">
                {loginType === 'admin' ? <Lock size={20} className="text-terra-light" /> : <User size={20} className="text-terra-light" />}
              </div>
              <div>
                <h3 className="font-display text-xl text-cream">
                  {loginType === 'admin' ? 'Espace Admin' : 'Espace Participant'}
                </h3>
                <p className="font-body text-xs text-cream/60">
                  {loginType === 'admin' ? 'Gérer les formations et participants' : 'Questionnaires et attestation'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Modal Body */}
          <form onSubmit={handleLogin} className="p-6 space-y-5">
            {loginType === 'admin' ? (
              <>
                <div>
                  <label className="form-label block mb-2">EMAIL</label>
                  <div className="relative">
                    <input
                      type="email"
                      className="form-input w-full pr-10"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      placeholder="votre@email.com"
                      autoFocus
                      data-testid="login-email-input"
                    />
                    <Mail size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-brown-light" />
                  </div>
                </div>
                <div>
                  <label className="form-label block mb-2">MOT DE PASSE</label>
                  <div className="relative">
                    <input
                      type="password"
                      className="form-input w-full pr-10"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      placeholder="••••••••"
                      data-testid="login-password-input"
                    />
                    <Lock size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-brown-light" />
                  </div>
                </div>
              </>
            ) : (
              <div>
                <label className="form-label block mb-2">VOTRE TOKEN D&apos;ACCÈS</label>
                <p className="font-body text-xs text-brown-mid mb-3">
                  Le token vous a été envoyé par email lors de votre inscription.
                </p>
                <input
                  type="text"
                  className="form-input w-full"
                  value={loginData.token}
                  onChange={(e) => setLoginData({ ...loginData, token: e.target.value })}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  autoFocus
                  data-testid="login-token-input"
                />
              </div>
            )}
            
            <button
              type="submit"
              disabled={isLoggingIn}
              className="cta-button w-full flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="login-submit-button"
            >
              {isLoggingIn ? 'Connexion...' : 'Se connecter'}
              <LogIn size={16} />
            </button>
            
            {loginType === 'student' && (
              <p className="text-center text-xs text-brown-light">
                Vous n&apos;avez pas de token ?{' '}
                <button 
                  type="button"
                  onClick={() => { setShowLoginModal(false); scrollToBooking(); }}
                  className="text-terra hover:underline"
                >
                  Inscrivez-vous à la formation
                </button>
              </p>
            )}
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
